import React from 'react';
import { Link } from 'react-router-dom';
import BlogIcon, { BlogIconName } from './BlogIcon';
import { BlogLink, formatCopyright, isInternalHref, useBlogSettings } from './blogSettings';
import type { BlogPageVariant } from './BlogHeader';

const FooterLink: React.FC<{ link: BlogLink; className: string }> = ({ link, className }) =>
  isInternalHref(link.href) ? (
    <Link to={link.href} className={className}>
      {link.label}
    </Link>
  ) : (
    <a href={link.href} className={className}>
      {link.label}
    </a>
  );

/** دکمهٔ آیکونی شبکه/کانال؛ بدون آدرس فقط آیکون (بدون لینک) نمایش داده می‌شود */
const SocialButton: React.FC<{ href: string; label: string; icon: BlogIconName; w: number; h: number; className: string }> = ({ href, label, icon, w, h, className }) => {
  const content = <BlogIcon name={icon} width={w} height={h} />;
  if (!href) {
    return (
      <span className={className} aria-hidden>
        {content}
      </span>
    );
  }
  const external = !isInternalHref(href) && !href.startsWith('mailto:') && !href.startsWith('tel:');
  return isInternalHref(href) ? (
    <Link to={href} aria-label={label} title={label} className={className}>
      {content}
    </Link>
  ) : (
    <a href={href} aria-label={label} title={label} className={className} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
      {content}
    </a>
  );
};

/** فوتر اختصاصی وبلاگ طبق طرح؛ چهار چیدمان (فهرست/مقاله × دسکتاپ/موبایل) */
const BlogFooter: React.FC<{ variant: BlogPageVariant }> = ({ variant }) => {
  const s = useBlogSettings();
  const copyright = formatCopyright(s.copyright);
  const links = s.footerLinks;
  const isDetail = variant === 'detail';

  return (
    <footer className={isDetail ? 'md:pt-10' : ''}>
      {/* ───── دسکتاپ ───── */}
      <div className="hidden border-t border-[#e9e8e5] bg-[#f4f3f1] px-10 md:block">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between gap-6 px-8 py-10">
          {/* برند و شعار (راست) */}
          <div className="flex flex-col items-start gap-1">
            <span className={`text-[18px] font-bold text-[#331751] ${isDetail ? 'leading-[30px]' : 'leading-7'}`}>{s.brandName}</span>
            <span className={`text-[14px] text-[#52575e] ${isDetail ? 'leading-[26px] text-[#5b5f66]' : 'leading-5'}`}>{s.footerTagline}</span>
            {isDetail && <span className="pt-2 text-[12px] font-medium leading-5 text-[#5b5f66]">{copyright}</span>}
          </div>

          {/* لینک‌ها */}
          <nav aria-label="لینک‌های فوتر" className={`flex flex-wrap items-center justify-center ${isDetail ? 'gap-4' : 'gap-6'}`}>
            {links.map((link) => (
              <FooterLink
                key={`${link.label}-${link.href}`}
                link={link}
                className={`rounded-[4px] transition-colors hover:text-[#331751] ${
                  isDetail ? 'text-[12px] font-medium leading-5 text-[#5b5f66]' : 'text-[14px] leading-5 text-[#52575e]'
                }`}
              />
            ))}
          </nav>

          {/* شبکه‌ها و کپی‌رایت (چپ) — فقط صفحهٔ فهرست */}
          {!isDetail && (
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-3">
                <SocialButton href={s.social.telegram} label="تلگرام" icon="social-telegram" w={12.667} h={10.667} className="flex h-8 w-8 items-center justify-center rounded-full border border-[#e9e8e5] hover:bg-white" />
                <SocialButton href={s.social.instagram} label="اینستاگرام" icon="social-instagram" w={13.333} h={12} className="flex h-8 w-8 items-center justify-center rounded-full border border-[#e9e8e5] hover:bg-white" />
                <SocialButton href={s.social.youtube} label="یوتیوب" icon="social-youtube" w={13.333} h={13.333} className="flex h-8 w-8 items-center justify-center rounded-full border border-[#e9e8e5] hover:bg-white" />
              </div>
              <span className="text-[12px] leading-4 text-[#52575e]">{copyright}</span>
            </div>
          )}
        </div>
      </div>

      {/* ───── موبایل ───── */}
      {isDetail ? (
        <div className="border-t border-[#e9e8e5] bg-[#f4f3f1] px-4 pb-6 pt-6 md:hidden">
          <div className="flex flex-col items-center gap-4">
            <div className="flex flex-col items-center gap-1 text-center">
              <span className="text-[18px] font-bold leading-[30px] text-[#331751]">{s.brandName}</span>
              <span className="max-w-[280px] text-[12px] font-medium leading-5 text-[#5b5f66]">{s.footerTaglineMobileDetail}</span>
            </div>
            <nav aria-label="لینک‌های فوتر" className="flex flex-wrap justify-center gap-x-4 gap-y-2">
              {links.map((link) => (
                <FooterLink key={`${link.label}-${link.href}`} link={link} className="text-[12px] font-medium leading-5 text-[#5b5f66]" />
              ))}
            </nav>
            <div className="flex items-center justify-center gap-3 pt-1">
              <SocialButton href={s.social.podcast} label="کانال پادکست" icon="footer-podcast-sm" w={15} h={15} className="flex h-8 w-8 items-center justify-center rounded-full bg-[#efeeeb]" />
              <SocialButton href={s.social.email} label="خبرنامه ایمیلی" icon="footer-mail" w={15} h={12} className="flex h-8 w-8 items-center justify-center rounded-full bg-[#efeeeb]" />
              <SocialButton href={s.social.rss} label="کانال اشتراک محتوا" icon="footer-rss" w={12.75} h={12.75} className="flex h-8 w-8 items-center justify-center rounded-full bg-[#efeeeb]" />
            </div>
            <div className="w-full border-t border-[#efeeeb] pt-2 text-center text-[14px] leading-[26px] text-[rgba(91,95,102,0.8)]">{copyright}</div>
          </div>
        </div>
      ) : (
        <div className="border-t border-[#e9e8e5] bg-[#f4f3f1] px-4 pb-8 pt-8 md:hidden">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-[5.375px]">
              <div className="flex items-center gap-[6px]">
                <span className="text-[18px] font-bold leading-[30px] text-[#331751]">{s.brandName}</span>
                <span className="h-[6px] w-[6px] rounded-full bg-[#4a2e68]" aria-hidden />
              </div>
              <p className="text-[14px] leading-[22.75px] text-[#5b5f66]">{s.footerTaglineMobileList}</p>
            </div>
            <nav aria-label="لینک‌های فوتر" className="grid grid-cols-2 gap-x-4 gap-y-[10px] border-t border-[#efeeeb] pt-2">
              {links.map((link, index) => (
                <FooterLink
                  key={`${link.label}-${link.href}`}
                  link={link}
                  className={`text-[12px] font-medium leading-5 text-[#5b5f66] ${index === links.length - 1 && links.length % 2 === 1 ? 'col-span-2' : ''}`}
                />
              ))}
            </nav>
            <div className="flex items-center gap-3 border-t border-[#efeeeb] pt-3">
              <SocialButton href={s.social.telegram} label="کانال تلگرام" icon="footer-telegram" w={15.833} h={13.333} className="flex h-10 w-10 items-center justify-center rounded-[8px] bg-[#efeeeb]" />
              <SocialButton href={s.social.instagram} label="صفحه اینستاگرام" icon="footer-instagram" w={16.667} h={15} className="flex h-10 w-10 items-center justify-center rounded-[8px] bg-[#efeeeb]" />
              <SocialButton href={s.social.podcast} label="پادکست‌ها" icon="footer-podcast" w={16.667} h={16.667} className="flex h-10 w-10 items-center justify-center rounded-[8px] bg-[#efeeeb]" />
              <SocialButton href={s.social.email} label="خبرنامه ایمیلی" icon="footer-email" w={16.667} h={16.667} className="flex h-10 w-10 items-center justify-center rounded-[8px] bg-[#efeeeb]" />
            </div>
            <div className="border-t border-[#efeeeb] pt-4 text-center text-[12px] font-medium leading-[18px] text-[rgba(91,95,102,0.8)]">{copyright}</div>
          </div>
        </div>
      )}
    </footer>
  );
};

export default BlogFooter;
